export type ModelId = string & { readonly __brand: 'ModelId' }
export type Slug = string & { readonly __brand: 'Slug' }
// const INVALID_ID_CHARS_REGEX = /[^a-zA-Z0-9-]/gi
const SLUG_SEPARATOR = '-'

/**
 * A map of symbols to their word equivalents for URL-friendly slugs and IDs.
 * This is the single source of truth for translations.
 */
const SYMBOL_MAP = new Map([
  ['+', 'plus'],
  ['&', 'and'],
  ['@', 'at'],
  // Add other symbol translations here (e.g., ['€', 'euro'])
])

const SYMBOL_REGEX = new RegExp(
  Array.from(SYMBOL_MAP.keys())
    .map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape special regex characters
    .join('|'),
  'g'
)

/**
 * A shared helper to apply the symbol-to-word translations.
 * @param input The string to process.
 * @returns The string with symbols like '+' replaced by 'plus'.
 */
function applySymbolTranslations(input: string): string {
  return input.replace(SYMBOL_REGEX, (match) => SYMBOL_MAP.get(match)!)
}

/**
 * Sanitizes any string to its canonical ID-compatible form.
 * It removes all characters that are not letters, numbers, or one of "+_()".
 * @param input The string to sanitize.
 * @returns The sanitized, lowercase string.
 */
// function sanitizeForId(input: string | undefined | null): string {
//   if (!input) {
//     return ''
//   }
//   return input.replace(INVALID_ID_CHARS_REGEX, '').toLowerCase()
// }

/**
 * Generates a unique and reliable ModelId from a brand and model.
 * This is a backwards-compatible, drop-in replacement for your original `getBrandModelId`.
 *
 * @param brand The product brand (e.g., "Konica Minolta").
 * @param model The product model (e.g., "bizhub 4050i").
 * @returns A reliably formatted ModelId.
 */
export function generateModelId(brand: string, model: string): ModelId {
  // Combine first, then sanitize, to match the original function's behavior.
  const combined = `${brand || ''}${model || ''}`.toLowerCase()
  const translated = applySymbolTranslations(combined)
  return translated.replace(/[^a-z0-9]/g, '') as ModelId
}

/**
 * Creates a URL-friendly slug from a string, ensuring it can be reversed back to an ID.
 * It replaces spaces and other invalid characters with a hyphen.
 * To ensure reversibility, it preserves the special characters "+_()" which are part of the ID.
 *
 * @param input The string to convert into a slug (e.g., "bizhub 4050i").
 * @returns A URL-friendly Slug.
 */
export function generateSlug(input: string | undefined | null): Slug {
  if (!input) {
    return '' as Slug
  }

  const slug = applySymbolTranslations(input.trim().toLowerCase())
    // Replace any remaining non-alphanumeric characters with a hyphen.
    .replace(/[^a-z0-9]+/g, SLUG_SEPARATOR)
    // Clean up leading/trailing hyphens.
    .replace(new RegExp(`^${SLUG_SEPARATOR}+|${SLUG_SEPARATOR}+$`, 'g'), '')

  return slug as Slug
}

/**
 * Creates a URL-friendly and S3-safe key from a string, intelligently
 * preserving file extensions. It applies symbol-to-word translations (e.g., '+' -> 'plus').
 *
 * @param input The string to convert into a safe key (e.g., "Product Spec v4+.pdf").
 * @returns A safe key (e.g., "product-spec-v4-plus.pdf").
 */
export function generateSafeKey(input: string | undefined | null): string {
  if (!input) {
    return ''
  }

  const trimmedInput = input.trim()
  const lastDotIndex = trimmedInput.lastIndexOf('.')

  let basename = trimmedInput
  let extension = ''

  // Check if there's a dot and it's not the first character
  if (lastDotIndex > 0) {
    basename = trimmedInput.substring(0, lastDotIndex)
    extension = trimmedInput.substring(lastDotIndex).toLowerCase() // Keep extension lowercase
  }

  const slug = applySymbolTranslations(basename.toLowerCase())
    // Replace any remaining non-alphanumeric characters with a hyphen.
    .replace(/[^a-z0-9]+/g, '-')
    // Clean up leading/trailing and multiple hyphens.
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')

  // Re-attach the extension if it exists.
  return slug + extension
}

/**
 * Reconstructs a ModelId from a page slug.
 * This works by stripping away all slug-specific characters (like '-' and '/')
 * and keeping only the canonical ID characters.
 *
 * @param slug A full or partial page slug (e.g., "konica-minolta/bizhub-4050i").
 * @returns The reconstructed ModelId.
 */
export function getModelIdFromSlug(slug: string | undefined | null): ModelId {
  if (!slug) {
    return '' as ModelId
  }
  // The same core sanitize function does the job perfectly.
  return slug.replace(/[/-]/g, '') as ModelId
}
